import React from "react";
import { GraduationCapIcon, MapPinIcon, SchoolIcon } from "lucide-react";
import { Student } from "@/types/Student";
import { Badge } from "@/components/ui";
import { useTranslation } from "react-i18next";
import { InterestPill } from "@/components/InterestPill";
import { useProtectedNavigation } from "@/routes/useProtectedNavigation";
import { StudentProfileImage } from "./StudentProfileImage";
import { StudentHeader } from "./StudentHeader";
import { AnalyticsEvent } from "@/hooks/useAnalytics";

export const StudentCard = ({
  student,
  onClick,
  profileSource,
}: {
  student: Partial<Student>;
  onClick?: () => void;
  profileSource?: string;
}) => {
  const { majors, interests, studentOrganizationStatus } = student;
  const { location, highSchool } = studentOrganizationStatus || {};
  const { navToRoute } = useProtectedNavigation();
  const { t } = useTranslation();

  return (
    <div
      className="flex h-full max-w-[350px] min-w-[260px] cursor-pointer flex-col gap-1 rounded-md border border-border bg-background p-4 hover:shadow-md"
      data-testid="student-grid-card"
      onClick={() => {
        if (student.id) {
          navToRoute(
            "student",
            { studentId: student.id },
            {},
            {
              newTab: true,
              analyticsEvent: profileSource ? AnalyticsEvent.OpenedProfile : undefined,
              analyticsProperties: profileSource ? { Source: profileSource } : undefined,
            },
          );
          onClick?.();
        }
      }}
    >
      <StudentProfileImage student={student} className="h-70" />

      <div className="flex flex-col gap-4 pt-2">
        <div className="flex flex-col gap-2">
          <StudentHeader student={student} />
          <div className="flex flex-col gap-3">
            <StudentDetailsParagraph
              icon={MapPinIcon}
              text={location || t("studentCard.noLocation")}
            />
            <StudentDetailsParagraph
              icon={SchoolIcon}
              text={highSchool || t("studentCard.noHighSchool")}
            />
            <StudentDetailsParagraph
              icon={GraduationCapIcon}
              text={
                majors?.length
                  ? majors.map((major) => major.name).join(", ")
                  : t("studentCard.noMajors")
              }
            />
          </div>
        </div>

        <div className="mt-2">
          {interests && interests.length > 0 ? (
            <div className="flex flex-wrap gap-x-1 gap-y-2">
              {interests.slice(0, 4).map((interest) => (
                <InterestPill key={interest.id} label={interest.name ?? ""} />
              ))}
              {interests.length > 4 && (
                <Badge
                  variant="outline"
                  className="rounded-full px-[11px] py-[3px] text-xs font-medium text-foreground"
                >
                  +{interests.length - 4}
                </Badge>
              )}
            </div>
          ) : (
            <Badge
              variant="dashed"
              className="rounded-full px-[11px] py-[3px] text-xs text-muted-foreground"
            >
              {t("studentCard.noInterests")}
            </Badge>
          )}
        </div>
      </div>
    </div>
  );
};

const StudentDetailsParagraph = ({
  icon: Icon,
  text,
}: {
  icon: React.ElementType;
  text: string;
}) => (
  <p className="text-md/4 flex items-center gap-1 text-muted-foreground">
    <Icon className="h-4 w-4 shrink-0" />
    <span className="truncate">{text}</span>
  </p>
);
